import { readProjectSettings } from '../admin/adminSettings';
import { readAdminSession } from '../admin/authProvider';

export type UsageFeature = {
    feature: string; users: number; activities: number; completedActivities: number;
    mobileUsers: number; desktopUsers: number; crossDeviceUsers: number;
    mobileUserRate: number; desktopUserRate: number;
};
export type UsageBreakdown = { feature: string; name: string; users: number; activities: number; userRate: number };
export type UsageDay = { feature: string; day: string; users: number; activities: number; completedActivities: number };
export type UsageInsightsData = {
    generatedAtUtc: string; fromUtc: string; toUtc: string; timeZone: string; dataQualityNote: string;
    features: UsageFeature[]; byDeviceClass: UsageBreakdown[]; byClientKind: UsageBreakdown[];
    byOperatingSystem: UsageBreakdown[]; byBrowser: UsageBreakdown[]; byDeviceModel: UsageBreakdown[]; daily: UsageDay[];
};

const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const list = (value: unknown) => Array.isArray(value) ? value : [];
const text = (value: unknown) => typeof value === 'string' ? value : '';
const number = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? value : 0;

function breakdown(value: unknown): UsageBreakdown {
    const row = record(value);
    return { feature: text(row.feature), name: text(row.name), users: number(row.users), activities: number(row.activities), userRate: number(row.userRate) };
}

function normalize(value: unknown): UsageInsightsData {
    const data = record(value);
    return {
        generatedAtUtc: text(data.generatedAtUtc), fromUtc: text(data.fromUtc), toUtc: text(data.toUtc),
        timeZone: text(data.timeZone), dataQualityNote: text(data.dataQualityNote),
        features: list(data.features).map((value) => { const row = record(value); return {
            feature: text(row.feature), users: number(row.users), activities: number(row.activities), completedActivities: number(row.completedActivities),
            mobileUsers: number(row.mobileUsers), desktopUsers: number(row.desktopUsers), crossDeviceUsers: number(row.crossDeviceUsers),
            mobileUserRate: number(row.mobileUserRate), desktopUserRate: number(row.desktopUserRate),
        }; }),
        byDeviceClass: list(data.byDeviceClass).map(breakdown), byClientKind: list(data.byClientKind).map(breakdown),
        byOperatingSystem: list(data.byOperatingSystem).map(breakdown), byBrowser: list(data.byBrowser).map(breakdown),
        byDeviceModel: list(data.byDeviceModel).map(breakdown),
        daily: list(data.daily).map((value) => { const row = record(value); return {
            feature: text(row.feature), day: text(row.day), users: number(row.users), activities: number(row.activities), completedActivities: number(row.completedActivities),
        }; }),
    };
}

export async function getUsageInsights(query: { fromUtc: string; toUtc: string; timeZone: string }) {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const configuredBase = String(import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? settings.adminApiBaseUrl ?? 'https://test-api.pteultra.com/api/v1').replace(/\/+$/, '');
    const version = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const root = /\/api\/v[^/]+$/i.test(configuredBase) ? configuredBase : `${configuredBase}/api/v${version}`;
    const url = new URL(`${root}/AdminUsageInsights`);
    Object.entries(query).forEach(([key, value]) => url.searchParams.set(key, value));
    const token = String(session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '');
    const headers = new Headers({ Accept: 'application/json' });
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const response = await fetch(url, { headers });
    const payload: unknown = (response.headers.get('content-type') ?? '').includes('application/json') ? await response.json() : await response.text();
    if (!response.ok) throw new Error(typeof payload === 'string' ? payload : `Usage insights request failed (${response.status})`);
    const envelope = record(payload);
    return normalize('data' in envelope ? envelope.data : payload);
}
